import React, { useMemo } from 'react';
import { MdOutlineAccountBalance } from 'react-icons/md';
import { SectionCard, StatTile, SubHeading, Note } from './ui';
import ChartFrame from '../charts/ChartFrame';
import StackedColumns from '../charts/StackedColumns';
import {
  fmtCompact, fmtNum, fmtPct, fmtMoney, fmtMonthShort, fmtMonth, fmtYear, fmtSigned, seriesSorted, seriesLatest, seriesPriorValue, seriesValueNear,
  compareToReference, pctChange, safeArray, isNum, PERMIT_LABELS, DASH,
} from '../format';

const PERMIT_KEYS = ['one_unit', 'two_unit', 'three_to_four_unit', 'five_plus_unit'];
const PERMIT_COLORS = ['var(--le-ramp-1)', 'var(--le-ramp-2)', 'var(--le-ramp-3)', 'var(--le-ramp-4)'];
const PERMIT_MONTHS = 24;

function tile({ label, points, format, natPoints, invert = false, yearsBack = 5, trendOnly = false, unitNote }) {
  const latest = seriesLatest(points);
  if (!latest) return { label, value: DASH, sub: 'No series', spark: [] };
  const prior = seriesPriorValue(points, yearsBack);
  const change = prior ? pctChange(prior.value, latest.value) : null;
  const nat = natPoints ? seriesValueNear(natPoints, latest.date) : null;
  const cmp = nat ? compareToReference(latest.value, nat.value) : null;
  let delta = null;
  if (cmp) {
    const tone = cmp.tone === 'flat' ? 'flat' : (cmp.tone === 'up') !== invert ? 'up' : 'down';
    delta = { label: `${cmp.label.replace('vs US', 'vs US county avg')}`, tone };
  } else if (isNum(change)) {
    const tone = Math.abs(change) < 1 ? 'flat' : (change > 0) !== invert ? 'up' : 'down';
    delta = { label: `${fmtSigned(change, 1, '%')} over ${yearsBack} yrs`, tone };
  }
  const spark = seriesSorted(points).slice(-16).map((p) => p.value);
  const sub = [
    fmtYear(latest.date) !== DASH ? (latest.date.length > 7 && !/-01-01$/.test(latest.date) ? fmtMonth(latest.date) : fmtYear(latest.date)) : null,
    cmp && isNum(change) ? `${fmtSigned(change, 1, '%')} / ${yearsBack} yrs` : null,
    trendOnly ? 'trend only' : null,
    unitNote || null,
  ].filter(Boolean).join(' · ');
  return { label, value: trendOnly ? (isNum(change) ? fmtSigned(change, 1, '%') : DASH) : format(latest.value), sub, delta: trendOnly ? null : delta, spark };
}

export default function CountyEconomyCard({ data, status }) {
  const county = data?.county || {};

  const tiles = useMemo(() => {
    const series = data?.series || {};
    const natSeries = data?.national_reference_series?.series || {};
    return [
      tile({ label: 'Population', points: series.resident_population_people, format: (v) => fmtCompact(v), natPoints: null }),
      tile({ label: 'Median household income', points: series.median_household_income_usd, format: (v) => fmtMoney(v, { compact: true }), natPoints: natSeries.median_household_income_usd }),
      tile({ label: 'Unemployment rate', points: series.unemployment_rate_percent, format: (v) => fmtPct(v, 1), natPoints: natSeries.unemployment_rate_percent, invert: true }),
      tile({ label: 'Private establishments', points: series.private_establishments, format: (v) => fmtCompact(v), natPoints: null }),
      tile({ label: 'Subprime credit share', points: series.subprime_credit_population_percent, format: (v) => fmtPct(v, 1), natPoints: natSeries.subprime_credit_population_percent, invert: true }),
      tile({ label: 'GDP, all industries', points: series.gdp_all_industries, format: (v) => fmtCompact(v), trendOnly: true }),
    ];
  }, [data]);

  const permitModel = useMemo(() => {
    const permits = safeArray(data?.building_permits?.monthly).slice().sort((a, b) => (a.month < b.month ? -1 : 1));
    const recent = permits.slice(-PERMIT_MONTHS);
    if (!recent.length) return null;
    const last12 = permits.slice(-12);
    const prior12 = permits.slice(-24, -12);
    const sum = (rows, key) => rows.reduce((s, r) => s + (isNum(key ? r.units_by_category?.[key] : r.total_units) ? (key ? r.units_by_category[key] : r.total_units) : 0), 0);
    const total12 = sum(last12);
    const totalPrior = prior12.length === 12 ? sum(prior12) : null;
    const fivePlus12 = sum(last12, 'five_plus_unit');
    const single12 = sum(last12, 'one_unit');
    return {
      categories: recent.map((r) => fmtMonthShort(r.month)),
      layers: PERMIT_KEYS.map((key, i) => ({ id: key, label: PERMIT_LABELS[key], color: PERMIT_COLORS[i], values: recent.map((r) => (isNum(r.units_by_category?.[key]) ? r.units_by_category[key] : 0)) })),
      rows: recent.slice().reverse().map((r) => ({ month: r.month, ...PERMIT_KEYS.reduce((acc, k) => ({ ...acc, [k]: r.units_by_category?.[k] }), {}), total: r.total_units })),
      total12,
      totalPrior,
      change: totalPrior ? pctChange(totalPrior, total12) : null,
      fivePlusShare: total12 > 0 ? (fivePlus12 / total12) * 100 : null,
      singleShare: total12 > 0 ? (single12 / total12) * 100 : null,
      through: recent[recent.length - 1].month,
    };
  }, [data]);

  const subtitle = data
    ? `${county.county_name || 'County'}${county.state_code ? `, ${county.state_code}` : ''} · FIPS ${county.county_fips || DASH} · federal series with the unweighted US-county average as yardstick`
    : null;

  return (
    <SectionCard id="le-section-economy" icon={<MdOutlineAccountBalance size={20} />} title="County economy" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          <div className="le-stat-grid le-stat-grid-3">
            {tiles.map((t, i) => <StatTile key={t.label} label={t.label} value={t.value} sub={t.sub} delta={t.delta} spark={t.spark} emphasis={i === 0} />)}
          </div>

          {permitModel ? (
            <>
              <SubHeading>Building permits · supply pipeline</SubHeading>
              <div className="le-stat-row">
                <StatTile label="Units permitted, last 12 months" value={fmtNum(permitModel.total12)} delta={isNum(permitModel.change) ? { label: `${fmtSigned(permitModel.change, 0, '%')} vs prior 12`, tone: permitModel.change > 3 ? 'up' : permitModel.change < -3 ? 'down' : 'flat' } : null} sub={`through ${fmtMonth(permitModel.through)}`} />
                <StatTile label="Single-family share" value={fmtPct(permitModel.singleShare, 0)} sub="1-unit structures" />
                <StatTile label="5+ unit share" value={fmtPct(permitModel.fivePlusShare, 0)} sub="Multifamily structures" />
              </div>
              <ChartFrame
                subtitle={`Monthly units authorized by structure size · last ${PERMIT_MONTHS} months`}
                legend={permitModel.layers.map((l) => ({ id: l.id, label: l.label, color: l.color }))}
                table={{
                  columns: [
                    { key: 'month', label: 'Month', format: (v) => fmtMonth(v) },
                    ...PERMIT_KEYS.map((k) => ({ key: k, label: PERMIT_LABELS[k], align: 'right', format: (v) => fmtNum(v) })),
                    { key: 'total', label: 'Total', align: 'right', format: (v) => fmtNum(v) },
                  ],
                  rows: permitModel.rows,
                }}
                footnote={safeArray(data?.building_permits?.notes).join(' ') || null}
              >
                <StackedColumns categories={permitModel.categories} layers={permitModel.layers} yFormat={(v) => fmtNum(v)} height={200} />
              </ChartFrame>
            </>
          ) : (
            <Note tone="muted">No building-permit series returned for this county.</Note>
          )}

          {data.series_notes?.gdp_all_industries ? <div className="le-footnotes muted">{data.series_notes.gdp_all_industries}</div> : null}
          {data.national_reference_series?.note ? <div className="le-footnotes muted">{data.national_reference_series.note}</div> : null}
        </>
      ) : null}
    </SectionCard>
  );
}
